import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Connector } from '@interfaces/connector';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ConnectorsService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {}

  /**
   * Returns an observable that retrieves the active connectors from the API.
   * @returns An observable that emits the active connectors.
   */
  getActiveConnectors = (companyUuid: string, paginationUrl?: string): Observable<any> => {
    const url = paginationUrl
      ? this.apiService.getCompanyConnectorsUrl() + '?status=active&company=' + companyUuid + paginationUrl
      : this.apiService.getCompanyConnectorsUrl() + '?status=active&company=' + companyUuid;
    return this.http.get(url);
  };

  /**
   * Switches the enabled state of a connector.
   * @param connectorUuid - The UUID of the connector.
   * @param enable - The desired enabled state.
   * @returns An Observable that emits the response from the server.
   */
  switchIsEnabledConnector = (connectorUuid: string, enable: boolean): Observable<any> =>
    this.http.patch(this.apiService.getCompanyConnectorsEnableUrl(connectorUuid), {
      is_enabled: enable,
    });

  /**
   * Returns an Observable that fetches the connector with the given UUID from the server.
   * @param connectorUuid The UUID of the connector to fetch.
   * @returns An Observable that emits the fetched connector data.
   */
  getConnectorByUUID = (connectorUuid: string): Observable<any> =>
    this.http.get(this.apiService.getConnectorDetailsUrl(connectorUuid));

  /**
   * Returns a string that fetches the connector authentication url with the given UUID from the server.
   * @param connectorUuid The UUID of the connector to fetch.
   * @param userUuid The UUID of the user to authenticate.
   * @returns An Observable that emits the fetched connector data.
   */
  getAuthenticateConnector = (connectorUuid: string, userUuid: string, companyUuid: string): string =>
    this.apiService.getConnectorDetailsUrl(connectorUuid) +
    `authenticate/?user_uuid=${userUuid}&company_uuid=${companyUuid}`;

  /**
   * Returns an Observable that fetches the connector unauthentication url with the given UUID from the server.
   * @param connectorUuid The UUID of the connector to fetch.
   * @returns An Observable that emits the fetched connector data.
   */
  getUnAuthenticateConnector = (connectorUuid: string, userUuid: string, companyUuid: string): string =>
    this.apiService.getConnectorDetailsUrl(connectorUuid) +
    `unauthenticate/?user_uuid=${userUuid}&company_uuid=${companyUuid}`;

  /**
   * Returns an Observable that retrieves a connector from the server.
   * @param uuid - The UUID of the connector to retrieve.
   * @returns An Observable that contains the connector.
   */
  getWorkspaceConnectorsUrl = (companyUuid: string, paginationUrl?: string): Observable<any> => {
    const url = paginationUrl
      ? this.apiService.getWorkspaceConnectorsUrl(companyUuid) + paginationUrl
      : this.apiService.getWorkspaceConnectorsUrl(companyUuid);
    return this.http.get(url);
  };

  /**
   * Returns an Observable that retrieves the list of connectors from the server.
   * @returns An Observable that contains the list of connectors.
   */
  getConnectors = (paginationUrl?: string): Observable<any> => {
    const url = paginationUrl ? this.apiService.getConnectorsUrl() + paginationUrl : this.apiService.getConnectorsUrl();
    return this.http.get(url);
  };

  /**
   * Returns an Observable that retrieves the list of connectors a workspace admin can create companyconnectors from.
   * @returns An Observable that contains the list of connectors.
   */
  getConnectorsForCompany = (companyUuid: string): Observable<any> => {
    return this.http.get(this.apiService.getWorkspaceAvailableConnectorsUrl(companyUuid));
  };

  /**
   * Add company connector
   * @param connector{ name: string,company: { uuid: string }, connector: { uuid: string }, status: "active", parent: { uuid: string }, connection_parameters: any, search_results_weight: number,} - The connector to add.
   * @returns An Observable that contains the added connector.
   */
  addCompanyConnector = (connector: Connector): Observable<any> => {
    return this.http.post(this.apiService.getCompanyConnectorsUrl(), connector);
  };

  /**
   * Updates an existing connector on the server.
   * @param updateConnector - The updated connector.
   * @returns An Observable that contains the updated connector.
   */
  updateCompanyConnector = (updateConnector: Connector, uuid: string): Observable<any> =>
    this.http.patch(this.apiService.getConnectorDetailsUrl(uuid), updateConnector);

  /**
   * Delete company connectors
   * @param uuid - The UUID of the company to delete.
   * @returns An observable of the HTTP response.
   */
  deleteCompanyConnectors = (uuid: string): Observable<any> =>
    this.http.delete(this.apiService.getConnectorDetailsUrl(uuid));

  /**
   * Copy company connectors from one company to another.
   * @param [uuid] - An array of the company uuids to copy.
   */
  createCompanyConnectors = (companyUuid: string, connectorsUuid: { uuid: string }[]): Observable<any> =>
    this.http.post(this.apiService.getCompanyConnectorsUrl(), {
      company: { uuid: companyUuid },
      connector: connectorsUuid,
    });

  /**
   * Copy company connectors from one company to another.
   * @param [uuid] - An array of the company uuids to copy.
   */
  copyCompanyConnectors = (companyUuid: string, connectorsUuid: { uuid: string }[]): Observable<any> =>
    this.http.post(this.apiService.geWorkspaceConnectorCopyUrl(), {
      company: { uuid: companyUuid },
      companyconnectors: connectorsUuid,
    });

  /**
   * Enable or disable all connectors
   * @param companyUuid - The UUID of the company to enable or disable all connectors.
   * @param enable - The desired enabled state.
   * @returns An Observable that emits the response from the server.
   */
  toggleAllConnectors = (companyUuid: string, enable: boolean): Observable<any> =>
    this.http.post(this.apiService.getCompanyConnectorsAllToggleUrl(), {
      company: { uuid: companyUuid },
      is_enabled: enable,
    });
}
